/**
 * Uniform defaults of extra.json shaders (W2-01a preview-gl; formats-and-packaging.md P§5.2, rendering.md R§9).
 *
 * Phira reads defaults and editor hints from comments on uniform declarations:
 *
 *   uniform <float|vec2|vec4> <name>; // %<default>% <free text>
 *
 * matched with the pattern `uniform\s+(\w+)\s+(\w+);\s+//\s+%([^%]+)%` over the whole source (comments included, as in
 * Phira: a commented-out declaration still counts; a precision-qualified one, `uniform lowp float x; // %1%`, never
 * matches, so its default is not applied, and neither is one without a space before `//`). `vec2` defaults are
 * `x, y`, `vec4` defaults `r, g, b, a` (floats); any other type in such a comment is a hard error in Phira ("Unknown
 * type"), as is a number that does not parse. The free text after the closing `%` is not parsed by Phira; the editor
 * uses it as a range hint. The runner applies these defaults first, then the effect's vars, then `time`,
 * `screenTexture`, `screenSize` and `UVScale` (P§5.2 uniform order).
 */

export type DefaultType = 'float' | 'vec2' | 'vec4';

export interface UniformDefault {
  readonly name: string;
  readonly type: DefaultType;
  readonly value: number | readonly number[];
  /** Free text after the default (trimmed; '' when none), e.g. '0..1' or 'int 1..64'. */
  readonly hint: string;
}

export interface UniformDefaultError {
  readonly name: string;
  /** Declared type as written. */
  readonly type: string;
  readonly reason: 'unknownType' | 'badNumber' | 'wrongCount';
  /** Human-readable detail for the problems panel (technical, like a compile log). */
  readonly detail: string;
}

export interface ParsedUniformDefaults {
  /** In source order. */
  readonly defaults: readonly UniformDefault[];
  readonly errors: readonly UniformDefaultError[];
}

/**
 * Phira's pattern with the Unicode classes of Rust's `regex` crate (its default): `\w` is
 * [\p{Alphabetic}\p{M}\p{Nd}\p{Pc}\p{Join_Control}] and `\s` is \p{White_Space} (JavaScript's `\s` differs: it has
 * U+FEFF and lacks U+0085). `[^%]` crosses line breaks in both.
 */
const W = String.raw`[\p{Alphabetic}\p{M}\p{Nd}\p{Pc}\p{Join_Control}]`;
const S = String.raw`\p{White_Space}`;
const PATTERN = new RegExp(String.raw`uniform${S}+(${W}+)${S}+(${W}+);${S}+//${S}+%([^%]+)%`, 'gu');

const COUNT: Readonly<Record<DefaultType, number>> = { float: 1, vec2: 2, vec4: 4 };

function isDefaultType(t: string): t is DefaultType {
  return t === 'float' || t === 'vec2' || t === 'vec4';
}

/** Rust `str::parse::<f32>` on a trimmed component (finite decimal or exponent forms, inf / nan words). */
export function parseF32(text: string): number | null {
  const s = text.trim();
  if (/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s)) return Math.fround(Number(s));
  const lower = s.toLowerCase().replace(/^\+/, '');
  if (lower === 'inf' || lower === 'infinity') return Infinity;
  if (lower === '-inf' || lower === '-infinity') return -Infinity;
  if (lower === 'nan' || lower === '-nan') return NaN;
  return null;
}

/** Parses every `// %default%` comment of a shader source. */
export function parseUniformDefaults(source: string): ParsedUniformDefaults {
  const defaults: UniformDefault[] = [];
  const errors: UniformDefaultError[] = [];
  for (const m of source.matchAll(PATTERN)) {
    const type = m[1] ?? '';
    const name = m[2] ?? '';
    const raw = m[3] ?? '';
    const end = m.index + m[0].length;
    const eol = source.indexOf('\n', end);
    const hint = source.slice(end, eol < 0 ? source.length : eol).trim();
    if (!isDefaultType(type)) {
      errors.push({
        name,
        type,
        reason: 'unknownType',
        detail: `uniform ${name}: unknown type '${type}' in a default comment`,
      });
      continue;
    }
    const parts = type === 'float' ? [raw] : raw.split(',');
    const values = parts.map(parseF32);
    if (values.some((v) => v === null)) {
      errors.push({
        name,
        type,
        reason: 'badNumber',
        detail: `uniform ${name}: default '${raw.trim()}' is not a number list`,
      });
      continue;
    }
    const nums = values as number[];
    if (nums.length !== COUNT[type]) {
      errors.push({
        name,
        type,
        reason: 'wrongCount',
        detail: `uniform ${name}: ${type} default needs ${String(COUNT[type])} values, got ${String(nums.length)}`,
      });
      continue;
    }
    defaults.push({ name, type, value: type === 'float' ? (nums[0] ?? 0) : nums, hint });
  }
  return { defaults, errors };
}
